import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { env } from "@/app/_utils/env";
import { postDjangoLogin } from "@/app/_services/loginApi";
import type { ClientSession, DjangoLoginPayload } from "@/app/_utils/authTypes";

export type { ClientSession, DjangoLoginPayload } from "@/app/_utils/authTypes";

const UI_USER_KEY = "cuo_user_profile";

type SessionAuthContextValue = {
  session: ClientSession | null;
  status: "loading" | "authenticated" | "unauthenticated";
  login: (
    username: string,
    password: string
  ) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  getSession: () => Promise<ClientSession | null>;
};

const SessionAuthContext = createContext<SessionAuthContextValue | null>(null);

function readStoredProfile(): DjangoLoginPayload | null {
  try {
    const raw = sessionStorage.getItem(UI_USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DjangoLoginPayload;
  } catch {
    return null;
  }
}

export function SessionAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [session, setSession] = useState<ClientSession | null>(() => {
    const p = readStoredProfile();
    return p ? { user: p, accessToken: p.auth } : null;
  });

  const status: "loading" | "authenticated" | "unauthenticated" = session
    ? "authenticated"
    : "unauthenticated";

  const login = useCallback(async (username: string, password: string) => {
    const { ok, body } = await postDjangoLogin(username, password);
    if (!ok || !body || typeof body !== "object" || !("user" in body)) {
      return {
        ok: false,
        error:
          body && typeof body === "object" && "message" in body
            ? String((body as { message?: string }).message)
            : "Invalid credentials",
      };
    }
    const payload = body as DjangoLoginPayload;
    sessionStorage.setItem(UI_USER_KEY, JSON.stringify(payload));
    const next: ClientSession = {
      user: payload,
      accessToken: payload.auth,
    };
    setSession(next);
    return { ok: true };
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${env.apiBaseUrl}auth/logout/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
    } finally {
      sessionStorage.removeItem(UI_USER_KEY);
      setSession(null);
    }
  }, []);

  const getSession = useCallback(async () => session, [session]);

  const value = useMemo(
    () => ({ session, status, login, logout, getSession }),
    [session, status, login, logout, getSession]
  );

  return (
    <SessionAuthContext.Provider value={value}>
      {children}
    </SessionAuthContext.Provider>
  );
}

export function useSessionAuth() {
  const ctx = useContext(SessionAuthContext);
  if (!ctx) {
    throw new Error("useSessionAuth must be used within SessionAuthProvider");
  }
  return ctx;
}

/** Drop-in replacement for `getSession` from next-auth/react */
export async function getSession(): Promise<ClientSession | null> {
  try {
    const raw = sessionStorage.getItem(UI_USER_KEY);
    if (!raw) return null;
    const user = JSON.parse(raw) as DjangoLoginPayload;
    return { user, accessToken: user.auth };
  } catch {
    return null;
  }
}

export function signOut() {
  sessionStorage.removeItem(UI_USER_KEY);
  void fetch(`${env.apiBaseUrl}auth/logout/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  }).finally(() => {
    window.location.href = "/login";
  });
}
